import { useEffect, useState } from 'react'
import myPartnersService from 'services/myPartnersService'

export interface PartnerProfile {
	_id: string
	name?: string
	firstName?: string
	title?: string
	company?: string
	email?: string
	phone?: string
	image?: string
}

/** One asset the two partners share. `direction` says whose it is. */
export interface SharedAsset {
	id: string
	name: string
	subtitle?: string
	direction: 'mine' | 'theirs'
	accessLevel: 'full' | 'readonly'
	/** Projects only: the location the project sits on. */
	locationId?: string | null
}

export interface SharedApproval {
	id: string
	evaluationId: string
	name: string
	decision: string
	direction: 'mine' | 'theirs'
}

export interface PartnerShared {
	partner: PartnerProfile
	isAccepted: boolean
	locations: SharedAsset[]
	projects: SharedAsset[]
	portfolios: SharedAsset[]
	approvals: SharedApproval[]
}

export type PartnerSharedState =
	| { status: 'idle' | 'loading' }
	| { status: 'ready'; data: PartnerShared }
	/**
	 * Not one of the viewer's partners. Partner lists on shared assets show
	 * everyone on the asset, including people the viewer has no partnership
	 * with, so this is an expected answer and not an error.
	 */
	| { status: 'not-partner' }
	| { status: 'error' }

/**
 * Loads `GET /api/mypartners/:partnerUserId/shared` for the partner profile
 * popup (MYD-533). Refetches when the id changes; a null id is idle.
 */
export function usePartnerShared(partnerUserId: string | null): PartnerSharedState {
	const [state, setState] = useState<PartnerSharedState>({ status: 'idle' })

	useEffect(() => {
		if (!partnerUserId) {
			setState({ status: 'idle' })
			return
		}
		let cancelled = false
		setState({ status: 'loading' })
		myPartnersService()
			.getPartnerShared(partnerUserId)
			.then((data: PartnerShared) => {
				if (!cancelled) setState({ status: 'ready', data })
			})
			.catch((err: any) => {
				if (cancelled) return
				setState({ status: err?.response?.status === 404 ? 'not-partner' : 'error' })
			})
		return () => {
			cancelled = true
		}
	}, [partnerUserId])

	return state
}

export default usePartnerShared
